import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import {
  ICustomisationCategory,
  IValidCombination,
  SelectedCategoriesAndOptions,
} from '@models/IProductCategoriesAndOptions';
import getValidProductCustomisationCategories from '@utils/helpers/getValidProductCustomisationCategories';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo } from 'react';
import ColourOptionsList from './ColourOptionsList';
import DefaultOptionsList from './DefaultOptionsList';

interface Props {
  readonly currentCategoriesAndOptionsSelection: SelectedCategoriesAndOptions;
  readonly customisationCategories: ICustomisationCategory[];
  readonly dataTestId?: string;
  readonly mainProductImage: IImage;
  readonly showMainProductImage: boolean;
  readonly title: string;
  readonly validCombinations?: IValidCombination[];
  readonly onOptionButtonClick: (
    categoriesAndOptionsSelection: SelectedCategoriesAndOptions,
    materialCode?: string | null
  ) => void;
}

const CategoriesAndOptionsSelector = ({
  currentCategoriesAndOptionsSelection,
  customisationCategories,
  dataTestId,
  mainProductImage,
  showMainProductImage,
  title,
  validCombinations,
  onOptionButtonClick,
}: Props) => {
  const hasSingleCustomisationCategory = customisationCategories.length === 1;
  const validCustomisationCategories = useMemo(
    () =>
      hasSingleCustomisationCategory || !validCombinations?.length
        ? customisationCategories
        : getValidProductCustomisationCategories(
            customisationCategories,
            validCombinations,
            currentCategoriesAndOptionsSelection
          ),
    [
      hasSingleCustomisationCategory,
      customisationCategories,
      validCombinations,
      currentCategoriesAndOptionsSelection,
    ]
  );

  const getMatchingMaterialCode = (
    categoriesAndOptionsSelection: SelectedCategoriesAndOptions
  ) => {
    const selectedOptions = Object.values(categoriesAndOptionsSelection);
    if (hasSingleCustomisationCategory) return selectedOptions[0]?.optionId;

    const matchedCombination = validCombinations?.find(
      ({ optionIds }) =>
        selectedOptions.length === optionIds.length &&
        selectedOptions.every((option) =>
          optionIds.includes(option?.optionId || '')
        )
    );
    return matchedCombination?.materialCode ?? null;
  };

  const handleOptionButtonClick = (
    selectedCategoryId: string,
    selectedCategoryName: string,
    selectedOptionId: string,
    selectedOptionName: string
  ) => {
    const categoriesIds = customisationCategories.map(
      (category) => category.categoryId
    );
    const selectedCategoryIndex = categoriesIds.indexOf(selectedCategoryId);

    const newCategoriesAndOptionsSelection = categoriesIds.reduce(
      (acc, categoryId, index) => {
        const getNewCategoryValue = () => {
          if (categoryId === selectedCategoryId) {
            return {
              categoryName: selectedCategoryName,
              optionId: selectedOptionId,
              optionName: selectedOptionName,
            };
          }

          return index < selectedCategoryIndex
            ? (currentCategoriesAndOptionsSelection[categoryId] ?? null)
            : null;
        };

        return {
          ...acc,
          [categoryId]: getNewCategoryValue(),
        };
      },
      {}
    );

    const materialCode = getMatchingMaterialCode(
      newCategoriesAndOptionsSelection
    );
    onOptionButtonClick(newCategoriesAndOptionsSelection, materialCode);
  };

  return (
    validCustomisationCategories.length > 0 && (
      <div data-testid={`${dataTestId}-Categories`}>
        <p className="h6 mb-7.5" data-testid={`${dataTestId}-Categories-Title`}>
          {title}
        </p>

        <div className="flex flex-col">
          {validCustomisationCategories.map(
            ({ categoryName, categoryId, categoryType, options }, index) => {
              const selectedOption = options.find(
                ({ materialCode, optionId }) =>
                  currentCategoriesAndOptionsSelection?.[categoryId]
                    ?.optionId ===
                  (hasSingleCustomisationCategory ? materialCode : optionId)
              );

              const optionsListProps = {
                categoryType,
                hasSingleCustomisationCategory,
                options,
                selectedOption,
                onOptionClick: (optionId: string, optionName: string) => {
                  handleOptionButtonClick(
                    categoryId,
                    categoryName,
                    optionId,
                    optionName
                  );
                },
              };
              const isColourCategory = options.some(
                (option) => !!option.colour || !!option.thumbnail
              );
              const isFirstCategory = index === 0;
              const isLastCategory =
                index === validCustomisationCategories.length - 1;

              return (
                <div key={categoryName}>
                  <div
                    data-testid={`${dataTestId}-Category-${index}`}
                    className={classNames({
                      'mb-10': isColourCategory && !isLastCategory,
                      'mb-13': !isColourCategory && !isLastCategory,
                    })}
                  >
                    <p
                      className={classNames(
                        'body-two text-grey-450',
                        isColourCategory ? 'mb-4' : 'mb-5'
                      )}
                      data-testid={`${dataTestId}-Category-${index}-Name`}
                    >
                      {categoryName}

                      {selectedOption?.name && (
                        <>
                          {': '}
                          <span className="body-two text-black-100">
                            {selectedOption.name}
                          </span>
                        </>
                      )}
                    </p>

                    {options?.length > 0 && (
                      <>
                        <div
                          className={isColourCategory ? 'lg:hidden' : 'hidden'}
                        >
                          <ColourOptionsList
                            {...optionsListProps}
                            dataTestId={`${dataTestId}-Category-${index}`}
                          />
                        </div>

                        <div
                          className={classNames({
                            'max-lg:hidden': isColourCategory,
                          })}
                        >
                          <DefaultOptionsList
                            {...optionsListProps}
                            dataTestId={`${dataTestId}-Category-${index}`}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {isFirstCategory && showMainProductImage && (
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={mainProductImage?.desktop?.url}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="mb-10 rounded-[10px] bg-grey-100 px-2.5 pt-[46px] pb-[57px] md:pb-[66px] lg:hidden"
                      >
                        <Image
                          image={mainProductImage}
                          imageClasses="object-contain h-[210px] md:h-[490px] lg:h-[770px]"
                          dataTestId={`${dataTestId}-Image`}
                        />
                      </motion.div>
                    </AnimatePresence>
                  )}
                </div>
              );
            }
          )}
        </div>
      </div>
    )
  );
};

export default CategoriesAndOptionsSelector;
